import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
    House,
    Cow,
    Grains,
    Scales,
    Drop,
    ClipboardText,
    Gear,
    SignOut,
    CircleNotch,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import { Logo } from "@/components/Logo";

export default function Layout() {
    const { user, logout } = useAuth();
    const { t, lang, setLang } = useT();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    const navItems = [
        { to: "/", label: t("nav_dashboard"), icon: House, testid: "nav-dashboard" },
        { to: "/animales", label: t("nav_animals"), icon: Cow, testid: "nav-animals" },
        { to: "/alimento", label: t("nav_feed"), icon: Grains, testid: "nav-feed" },
        { to: "/pesajes", label: t("nav_weighings"), icon: Scales, testid: "nav-weighings" },
        { to: "/leche", label: t("nav_milk"), icon: Drop, testid: "nav-milk" },
        { to: "/reportes", label: t("nav_reports"), icon: ClipboardText, testid: "nav-reports" },
        { to: "/ajustes", label: t("nav_settings"), icon: Gear, testid: "nav-settings" },
    ];

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar */}
            <aside
                className="hidden lg:flex flex-col w-64 shrink-0 text-white"
                style={{ background: "#1A3626" }}
                data-testid="app-sidebar"
            >
                <div className="px-5 py-6 border-b border-white/10">
                    <Logo
                        size={34}
                        bg="#22432F"
                        primary="#FFFFFF"
                        accent="#D99533"
                        wordmarkClass="text-white text-lg"
                        subtitle={t("tagline")}
                        subtitleClass="text-white/60"
                    />
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                            data-testid={item.testid}
                        >
                            <item.icon size={18} weight="regular" />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                {/* Language switcher */}
                <div className="px-4 pb-3">
                    <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">{t("language")}</div>
                    <div className="flex gap-1 rounded-md bg-white/5 p-1" data-testid="lang-switcher">
                        {["es", "en"].map((l) => (
                            <button
                                key={l}
                                onClick={() => setLang(l)}
                                className={`flex-1 text-xs font-medium py-1.5 rounded transition-colors ${lang === l ? "bg-white text-[#1A3626]" : "text-white/70 hover:text-white"}`}
                                data-testid={`lang-${l}`}
                            >
                                {l === "es" ? "Español" : "English"}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-4 border-t border-white/10">
                    <div className="text-xs text-white/60 mb-2">{t("session")}</div>
                    <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name || user?.email}</div>
                    <div className="text-xs text-white/60 truncate mb-3" data-testid="user-email">{user?.email}</div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-md bg-white/10 hover:bg-white/15 transition-colors"
                        data-testid="logout-btn"
                    >
                        <SignOut size={16} /> {t("logout")}
                    </button>
                </div>
            </aside>

            {/* Mobile header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#1A3626] text-white px-4 py-3 flex items-center justify-between">
                <Logo size={26} primary="#FFFFFF" accent="#D99533" wordmarkClass="text-white text-base" />
                <div className="flex items-center gap-2">
                    <select
                        value={lang}
                        onChange={(e) => setLang(e.target.value)}
                        className="bg-white/10 text-xs rounded px-2 py-1 border border-white/20"
                        data-testid="lang-switcher-mobile"
                    >
                        <option value="es">ES</option>
                        <option value="en">EN</option>
                    </select>
                    <button
                        onClick={handleLogout}
                        className="text-xs flex items-center gap-1 bg-white/10 px-3 py-1.5 rounded-md"
                        data-testid="logout-btn-mobile"
                    >
                        <SignOut size={14} /> {t("exit")}
                    </button>
                </div>
            </div>

            {/* Mobile bottom nav */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#1A3626] text-white flex justify-around py-2 border-t border-white/10 overflow-x-auto">
                {navItems.slice(0, 6).map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/"}
                        className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] px-2 py-1 rounded-md shrink-0 ${isActive ? "text-white" : "text-white/60"}`}
                    >
                        <item.icon size={18} />
                        {item.label}
                    </NavLink>
                ))}
            </div>

            {/* Main content */}
            <main className="flex-1 min-w-0 lg:pt-0 pt-14 pb-20 lg:pb-0">
                <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-10 animate-fade-in">
                    <Outlet />
                </div>
            </main>

            <Toaster position="top-right" />
        </div>
    );
}

export function PageLoading() {
    return (
        <div className="flex items-center gap-2 text-muted-foreground">
            <CircleNotch className="animate-spin" size={18} /> Cargando…
        </div>
    );
}
